import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure PDF.js worker via CDN (matches installed pdfjs-dist version)
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

export default function PdfViewer({ url, downloadUrl, filename = "document.pdf" }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.1);
  const [width, setWidth] = useState(700);
  const [error, setError] = useState("");

  useEffect(() => {
    const onResize = () => {
      const w = Math.min(window.innerWidth - 64, 900);
      setWidth(w);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="paper-card p-4 sm:p-6" data-testid="pdf-viewer">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
            className="p-2 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-50"
            data-testid="pdf-prev"
            aria-label="Page précédente"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-slate-600 min-w-[80px] text-center">
            {pageNumber} / {numPages || "–"}
          </span>
          <button
            onClick={() => setPageNumber((p) => Math.min(numPages || p, p + 1))}
            disabled={!numPages || pageNumber >= numPages}
            className="p-2 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-50"
            data-testid="pdf-next"
            aria-label="Page suivante"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)))}
            className="p-2 border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="pdf-zoom-out"
            aria-label="Réduire"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs text-slate-500 w-12 text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale((s) => Math.min(2.5, +(s + 0.15).toFixed(2)))}
            className="p-2 border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="pdf-zoom-in"
            aria-label="Agrandir"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          {(downloadUrl || url) && (
            <a
              href={downloadUrl || url}
              download={filename}
              className="btn-secondary text-xs ml-2"
              data-testid="pdf-download"
            >
              <Download className="w-3.5 h-3.5" /> Télécharger
            </a>
          )}
        </div>
      </div>

      <div className="bg-slate-100 rounded-sm p-2 sm:p-4 flex items-start justify-center overflow-auto max-h-[75vh]">
        {error ? (
          <div className="p-8 text-sm text-unisic-red text-center">{error}</div>
        ) : (
          <Document
            file={url}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={(e) => setError(`Impossible de charger le PDF (${e?.message || "erreur"}).`)}
            loading={<div className="p-10 text-sm text-slate-500">Chargement du document…</div>}
          >
            <Page
              pageNumber={pageNumber}
              width={width * scale}
              renderAnnotationLayer
              renderTextLayer
            />
          </Document>
        )}
      </div>
    </div>
  );
}
